import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { Button, Drawer, IconButton, Spinner, Tab, TabPanel, Tabs, TabsBody, TabsHeader, Typography } from '@material-tailwind/react'
import { useForm } from 'react-hook-form'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/store'
import useIsMobile from '../../../utils/useIsMobile'
import { FILTER_SCREEN_MOBILE, MOBILE_DIVIDER_OFFSET } from '../../../utils/variables'
import { isIterableArray, toFormatPrice } from '../../../utils/utils'
import CloseDrawerIcon from '../../../images/icons/CloseDrawerIcon'
import { useViewBalancesByContractQuery } from '../../../store/auth/authService'
import DownloadBtn from '../../tableHelpers/buttons/DownloadBtn'
import ExcelIcon from '../../../images/icons/ExcelIcon'
import FilterDayMonthYear from '../../tableHelpers/filters/FilterDayMonthYear'
import ArrowAccordionIcon from '../../../images/icons/ArrowAccordionIcon'
import TableBalance from './TableBalance'
import FilterSearch from '../../tableHelpers/filters/FilterSearch'
import axios from 'axios'
import { baseApi } from '../../../ConfigApi'
import { errorToast } from '../../../components/toasts/toasts'
import fileDownload from 'js-file-download'

type FormInput = {
    date_from: string,
    date_to: string,
    contract_id: number
}

const DrawerViewBalancesByContract = (props: {
    open: boolean, handleClose: any, balanceKey: number
}): any => {

    const { open, handleClose, balanceKey } = props

    const { t } = useTranslation()

    const { service_id, contract_id } = useSelector((state: RootState) => state.contract)

    const bgService = service_id === 1 ? ' !bg-orange-50 !text-orange-500 border-orange-200' : service_id === 2 ? ' !bg-blue-200 !text-blue-500 !border-blue-300' : ' bg-gray-500'
    const drawerTitle = service_id === 1 ? t('contract_page.view_balances_by_contract_drawer_title_ee') : service_id === 2 ? t('contract_page.view_balances_by_contract_drawer_title_gas') : ' '

    const isMobile = useIsMobile(FILTER_SCREEN_MOBILE)
    const placement = isMobile ? 'bottom' : 'right'
    const size = isMobile ? window.innerHeight - MOBILE_DIVIDER_OFFSET : 900

    const defaultDateFrom = moment().add(-1, 'month').format('DD-MM-YYYY')
    const defaultDateTo = moment().format('DD-MM-YYYY')

    const defaultState: FormInput = {
        date_from: defaultDateFrom,
        date_to: defaultDateTo,
        contract_id: Number(contract_id)
    }
    const {
        control,
        reset,
        setValue,
        getValues,
    } = useForm<FormInput>({
        defaultValues: defaultState,
    })

    const [sendForm, setSendForm] = useState<FormInput>(defaultState);

    const [filtersState, setFiltersState] = useState<FormInput>(defaultState);

    const { currentData, isLoading, isFetching, isError, refetch } = useViewBalancesByContractQuery(sendForm)

    const [search, setSearch] = useState<string>('');

    const [tabKey, setTabKey] = useState(balanceKey);

    useEffect(() => {
        if (balanceKey) setTabKey(balanceKey)
    }, [balanceKey]);


    const fetchPage = (values: any) => {
        setSendForm({
            date_from: moment(values.date_from, 'DD.MM.YYYY').format('DD-MM-YYYY'),
            date_to: moment(values.date_to, 'DD.MM.YYYY').format('DD-MM-YYYY'),
            contract_id: Number(contract_id)
        })
    }


    const handleCloseFilter = () => {
        const values = getValues()

        setFiltersState(values)

        if (JSON.stringify(values) !== JSON.stringify(filtersState)) {
            // fetch page with new filters 
            fetchPage(values)
        }
    }

    const handleDownload = (_: any, callback: any) => {

        const form = {
            date_from: sendForm.date_from,
            date_to: sendForm.date_to,
            contract_id: sendForm.contract_id
        }
        axios.post(`${baseApi}/api/ca/view-saldo-for-modal-excel`, form)
            .then((response: any) => {
                if (response.data.res === 'error') {
                    errorToast({
                        title: drawerTitle,
                        text: t('error_on_download')
                    })
                    if (callback) callback()
                }
                else {
                    setTimeout(() => {
                        axios.post(`${baseApi}/api/ca/view-saldo-for-modal-excel/export-file`, {
                            contract_id: sendForm.contract_id,
                            filename: response.data.nameFile
                        }, { responseType: 'blob', })
                            .then((response: any) => {
                                if (response.data.res === 'error') {
                                    errorToast({
                                        title: drawerTitle,
                                        text: t('error_on_download')
                                    })
                                }
                                else {
                                    fileDownload(response.data, `${t('analytics.analytics_saldo_title')}.xlsx`)
                                }
                            })
                            .catch(error => {
                                console.error(error);
                                errorToast({
                                    title: drawerTitle,
                                    text: t('error_on_download')
                                })
                            })
                            .finally(() => {
                                if (callback) callback()
                            })
                    }, 5000);

                }
            })
            .catch(error => {
                console.error(error);
                errorToast({
                    title: drawerTitle,
                    text: t('error_on_download')
                })
                if (callback) callback()
            })
            .finally(() => {
                if (callback) callback()
            })
    }




    return (
        createPortal(
            <Drawer
                placement={placement}
                size={size}
                open={open}
                onClose={handleClose}
                className={`p-4 ${open ? 'tab:!right-[12px] tab:!top-[12px] tab:!bottom-[-12px] opacity-1' : 'tab:!right-0 opacity-0 duration-[250ms]'} left-0 right-0  tab:!left-auto !bottom-0  tab:!max-w-[900px]  flex flex-col overflow-auto`}
            >
                <div className='flex flex-col h-[100%]'>
                    <div className="mb-6 flex items-center justify-between">
                        <Typography variant="lead" className='text-[18px] tab:text-[24px] font-medium' >
                            {drawerTitle}
                        </Typography>
                        <IconButton variant="text" color="blue-gray" className='hover:bg-gray-100 active:bg-gray-100 rounded-full' onClick={handleClose}>
                            <CloseDrawerIcon />
                        </IconButton>
                    </div>
                    <div className='flex flex-col justify-start grow gap-[24px]'>
                        <div className='flex flex-col-reverse tab:flex-row tab:items-center justify-between gap-[24px]'>
                            <div className='flex items-center gap-[8px]'>
                                <FilterDayMonthYear
                                    isLoadingFetch={false}
                                    control={control}
                                    fieldName={'date_from'}
                                    defaultValue={defaultState.date_from}
                                    filterStateValue={moment(filtersState.date_from, 'DD-MM-YYYY').format('DD MMMM YYYY')}
                                    handleCloseFilter={handleCloseFilter}
                                    valueLabelFormatter={(label: string) => {
                                        return moment(label, 'DD-MM-YYYY').format("DD MMMM YYYY")
                                    }}
                                    listClassName={'z-[10000]'}
                                    placement='bottom-start'
                                />
                                <span className='rotate-90'>
                                    <ArrowAccordionIcon />
                                </span>
                                <FilterDayMonthYear
                                    isLoadingFetch={false}
                                    control={control}
                                    fieldName={'date_to'}
                                    defaultValue={defaultState.date_to}
                                    filterStateValue={moment(filtersState.date_to, 'DD-MM-YYYY').format('DD MMMM YYYY')}
                                    handleCloseFilter={handleCloseFilter}
                                    valueLabelFormatter={(label: string) => {
                                        return moment(label, 'DD-MM-YYYY').format("DD MMMM YYYY")
                                    }}
                                    listClassName={'z-[10000]'}
                                />
                            </div>
                            <div className='hidden tab:block'>
                                <DownloadBtn
                                    size='lg'
                                    disabled={isFetching || !(isIterableArray(currentData?.[0]?.data) || isIterableArray(currentData?.[1]?.data))}
                                    icon={<ExcelIcon className='w-[25px]' />}
                                    tooltipText={t('contract_page.balances_download_excel')}
                                    record={{}}
                                    handleDownloadFile={handleDownload}
                                    tooltipClassName={'z-[10000]'}
                                />
                            </div>
                        </div>

                        <div className='flex'>
                            <FilterSearch
                                onSearch={(e: any) => { setSearch(e) }}
                            />
                        </div>
                        {
                            isFetching ?
                                <Spinner className='mx-auto' />
                                :
                                currentData ?
                                    <div className=' mb-[36px]'>

                                        {
                                            isIterableArray(currentData) &&
                                            <div className=''>
                                                <div className='flex gap-[8px] justify-between items-center '>
                                                    {currentData.map((item: any) => (
                                                        <div
                                                            key={item.balance}
                                                            onClick={() => {
                                                                setTabKey(item.balance)
                                                            }}
                                                            className={`cursor-pointer rounded bg-gray-100 text-gray-800  border-2 border-gray-100 ${item.balance === tabKey ? bgService : ''} transition-all h-full w-auto grow py-3 text-center`}
                                                        >
                                                            {t(`balance_${item.balance}`)}
                                                        </div>
                                                    ))}
                                                </div>
                                                {
                                                    currentData.map((item: any, key: number) => (
                                                        <div key={key} className={`p-0 mt-[8px] ${item.balance === tabKey ? '' : 'hidden'}`}>
                                                            {
                                                                isIterableArray(item.data) ?
                                                                    <TableBalance
                                                                        data={item.data}
                                                                        search={search}
                                                                    />
                                                                    :
                                                                    <div className='bg-white border-2 border-gray-100 rounded-[14px] tab:rounded-[24px] p-[12px]'>
                                                                        <div className='py-[10px]'>
                                                                            <p className={`text-center text-[14px] lap:text-[18px] text-gray-600`}>
                                                                                {t('contract_page.pay_plan_no_data')}
                                                                            </p>
                                                                        </div>
                                                                    </div>

                                                            }
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        }
                                    </div>
                                    :
                                    isError ?
                                        <div className='mx-auto'>
                                            <div className="flex flex-col items-center gap-[40px] my-[40px]">
                                                <Typography variant='lead' className='des:text-[20px] font-medium text-center '>
                                                    {t('tables.error_on_loading')}
                                                </Typography>
                                                <Button color='black' onClick={refetch}>
                                                    {t('reload')}
                                                </Button>
                                            </div>
                                        </div>
                                        :
                                        <>
                                        </>
                        }

                        <div className='flex flex-col gap-[10px] my-6 mt-auto tab:hidden '>
                            <DownloadBtn
                                size='lg'
                                disabled={isFetching}
                                icon={<ExcelIcon className='w-[25px]' />}
                                tooltipText={t('contract_page.balances_download_excel')}
                                record={{}}
                                handleDownloadFile={handleDownload}
                                tooltipClassName={'z-[10000]'}
                            />

                            <Button color='white' >
                                {t('close')}
                            </Button>
                        </div>
                    </div>
                </div>

            </Drawer>,
            document.getElementById('root')!
        )
    )
}

export default DrawerViewBalancesByContract